import React from "react";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { Maybe, WORKFLOW_KEYS } from "fond/types";

interface GeneratePanelProps {
  isDisabled?: boolean;
  versionId: Maybe<string>;
}

const stageConfig: Record<string, { label: string }> = {
  c2s: { label: "Calculating cost to serve" },
};

const GeneratePanel: React.FC<GeneratePanelProps> = ({ versionId, isDisabled }) => {
  const { workflowState, stages, runWorkflow } = useCustomWorkflow({
    versionId: versionId ?? undefined,
    key: WORKFLOW_KEYS.COLLABORATION_CTS.ROOT,
  });
  const isGenerating = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleGenerate = async () => {
    runWorkflow?.();
  };

  return (
    <>
      {workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Cost to serve outputs are ready.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Cost to serve outputs generated.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 2 }}>
          <AlertTitle>Cost to Serve failed</AlertTitle>
          Cost to serve outputs could not be generated.
        </Alert>
      )}

      {(isGenerating || workflowState === "ERROR") && (
        <>
          <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
            Generating cost to serve.
          </Alert>
          {stages ? (
            <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
          ) : (
            <Progress steps={[{ ID: "preparing", Label: "Preparing", Status: "InProgress" }]} />
          )}
        </>
      )}

      {!isGenerating && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || isGenerating || isDisabled}
          onClick={handleGenerate}
        >
          {workflowState === "COMPLETE" ? "Regenerate" : "Generate"} Cost to Serve
        </Button>
      )}
    </>
  );
};

export default GeneratePanel;
